import axios from '../axios';
import type { Pagination } from '../interfaces/api';
import { Device } from '../interfaces/devices';
import type { PlayHistoryObject } from '../interfaces/player';
import { store } from '../store/store';
import { spotifyActions } from '../store/slices/spotify';
import { homeActions } from '../store/slices/home';
import { playlistService } from './playlists';
import { historyService } from './history';
import { mapJioSaavnSongToSpotifyTrack } from '../utils/jiosaavn-adapters';

export const setPlaybackDevice = (deviceId: string | null) => {};
export const setPlaybackDeviceName = (name: string | null) => {};

const VOLUME_STORAGE_KEY = 'openmusic_volume';

export const getSavedVolume = (): number => {
  if (typeof window === 'undefined') return 1;
  try {
    const saved = localStorage.getItem(VOLUME_STORAGE_KEY);
    if (saved !== null) {
      const parsed = parseFloat(saved);
      if (!isNaN(parsed) && parsed > 0 && parsed <= 1) {
        return parsed;
      }
    }
    const raw = localStorage.getItem('openmusic_player_session');
    if (raw) {
      const session = JSON.parse(raw);
      if (typeof session.volume === 'number' && !isNaN(session.volume) && session.volume > 0) {
        return Math.max(0.05, Math.min(1, session.volume));
      }
    }
  } catch (e) {}
  return 1;
};

export const saveVolume = (vol: number) => {
  if (typeof window === 'undefined') return;
  try {
    if (vol > 0) {
      localStorage.setItem(VOLUME_STORAGE_KEY, String(vol));
    }
  } catch (e) {}
};

const audio = new window.Audio();
audio.volume = getSavedVolume();

// ---------------------------------------------------------
// NEW CORE DATA MODEL
// ---------------------------------------------------------
let sourceContext: any[] = [];
let shuffledSourceOrder: any[] = [];
let manualQueue: any[] = [];
let playbackHistory: any[] = [];

let currentTrackIndex: number = -1; // index within active context (sourceContext or shuffledSourceOrder)
let shuffleEnabled: boolean = false;
let repeatMode: 'off' | 'all' | 'one' = 'off';
let autoplayEnabled: boolean = true; // Radio fallback is permanently ON based on user specs

let currentTrack: any = null;
let isPlayingFromManualQueue: boolean = false;
let currentContextUri: string = '';
let isResolvingNextTrack: boolean = false;
let lastKnownPositionMs: number = 0;
let lastSaveTime: number = 0;

const STORAGE_KEY = 'openmusic_player_session';

interface SavedPlayerSession {
  currentTrack: any;
  sourceContext: any[];
  shuffledSourceOrder: any[];
  manualQueue: any[];
  playbackHistory: any[];
  currentTrackIndex: number;
  shuffleEnabled: boolean;
  repeatMode: 'off' | 'all' | 'one';
  isPlayingFromManualQueue: boolean;
  currentContextUri: string;
  position: number;
  volume: number;
}

const savePlaybackSession = (force: boolean = false) => {
  if (!currentTrack || typeof window === 'undefined') return;
  const now = Date.now();
  if (!force && now - lastSaveTime < 1000) return;
  lastSaveTime = now;

  try {
    const session: SavedPlayerSession = {
      currentTrack,
      sourceContext: sourceContext.slice(0, 500),
      shuffledSourceOrder: shuffledSourceOrder.slice(0, 500),
      manualQueue: manualQueue.slice(0, 100),
      playbackHistory: playbackHistory.slice(-50),
      currentTrackIndex,
      shuffleEnabled,
      repeatMode,
      isPlayingFromManualQueue,
      currentContextUri,
      position: audio.currentTime > 0 ? audio.currentTime : (lastKnownPositionMs / 1000),
      volume: audio.volume > 0 ? audio.volume : (getSavedVolume() || 1)
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch (e) {
    console.warn('Failed to save playback session to localStorage:', e);
  }
};

let sessionRestored = false;

const restorePlaybackSession = () => {
  if (typeof window === 'undefined') return;

  if (sessionRestored) {
    if (currentTrack) {
      dispatchState();
    }
    return;
  }

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const session: SavedPlayerSession = JSON.parse(raw);
    if (!session || !session.currentTrack) return;

    sessionRestored = true;
    currentTrack = session.currentTrack;
    sourceContext = Array.isArray(session.sourceContext) ? session.sourceContext : [];
    shuffledSourceOrder = Array.isArray(session.shuffledSourceOrder) ? session.shuffledSourceOrder : [];
    manualQueue = Array.isArray(session.manualQueue) ? session.manualQueue : [];
    playbackHistory = Array.isArray(session.playbackHistory) ? session.playbackHistory : [];
    currentTrackIndex = typeof session.currentTrackIndex === 'number' ? session.currentTrackIndex : -1;
    shuffleEnabled = !!session.shuffleEnabled;
    repeatMode = session.repeatMode || 'off';
    isPlayingFromManualQueue = !!session.isPlayingFromManualQueue;
    currentContextUri = session.currentContextUri || '';

    const effectiveVolume = (typeof session.volume === 'number' && !isNaN(session.volume) && session.volume > 0)
      ? Math.max(0.05, Math.min(1, session.volume))
      : getSavedVolume();

    audio.volume = effectiveVolume;
    store.dispatch(spotifyActions.setVolume({ volume: effectiveVolume }));

    const savedPosition = session.position && !isNaN(session.position) && session.position > 0 ? session.position : 0;
    lastKnownPositionMs = savedPosition * 1000;

    if (currentTrack.preview_url) {
      audio.src = currentTrack.preview_url;
      if (savedPosition > 0) {
        const applySeek = () => {
          try {
            audio.currentTime = savedPosition;
          } catch (e) {}
        };
        if (audio.readyState >= 1) {
          applySeek();
        } else {
          audio.addEventListener('loadedmetadata', applySeek, { once: true });
        }
      }
    }

    updateMediaSession(currentTrack);

    // Remain paused on reload so browser doesn't block autoplay
    audio.pause();
    dispatchState();
  } catch (e) {
    console.warn('Failed to restore playback session:', e);
  }
};

// Helpers
const getActiveContext = () => shuffleEnabled ? shuffledSourceOrder : sourceContext;

const generateNewShuffle = (excludeTrack?: any) => {
  const pool = [...sourceContext];
  
  if (excludeTrack) {
    const idx = pool.findIndex(t => t.id === excludeTrack.id);
    if (idx !== -1) pool.splice(idx, 1);
  }
  
  // Fisher-Yates shuffle
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  
  if (excludeTrack) {
    pool.unshift(excludeTrack);
  }
  
  shuffledSourceOrder = pool;
};

const dispatchState = () => {
  try {
    if (!store || typeof store.dispatch !== 'function') return;
    if (currentTrack) {
    // Calculate upcoming tracks for "Next in Queue" and "Next from Context"
    const activeContext = getActiveContext();
    const remainingContext = currentTrackIndex >= 0 && currentTrackIndex < activeContext.length - 1 
      ? activeContext.slice(currentTrackIndex + 1)
      : [];
    
    const nextTracks = [...manualQueue, ...remainingContext];

    // Map internal repeat mode string to Spotify Web SDK int (0: off, 1: context, 2: track)
    let repeat_mode = 0;
    if (repeatMode === 'all') repeat_mode = 1;
    if (repeatMode === 'one') repeat_mode = 2;

    const currentPosition = audio.seeking
      ? lastKnownPositionMs
      : (audio.currentTime > 0 ? audio.currentTime * 1000 : lastKnownPositionMs);

    store.dispatch(spotifyActions.setState({
      state: {
        context: { uri: currentContextUri } as any,
        disallows: {} as any,
        duration: currentTrack.duration_ms,
        paused: audio.paused,
        position: currentPosition,
        repeat_mode,
        shuffle: shuffleEnabled,
        track_window: {
          current_track: currentTrack,
          next_tracks: nextTracks,
          previous_tracks: [...playbackHistory]
        }
      } as unknown as Spotify.PlaybackState
    }));
    
    // Dispatch to queue slice
    store.dispatch({
      type: 'queue/fetchQueue/fulfilled',
      payload: nextTracks
    });

    if (typeof window !== 'undefined' && 'mediaSession' in navigator) {
      navigator.mediaSession.playbackState = audio.paused ? 'paused' : 'playing';
      if ('setPositionState' in navigator.mediaSession && audio.duration && !isNaN(audio.duration) && isFinite(audio.duration)) {
        try {
          navigator.mediaSession.setPositionState({
            duration: audio.duration,
            playbackRate: audio.playbackRate || 1,
            position: Math.min(Math.max(0, audio.currentTime), audio.duration),
          });
        } catch (e) {}
      }
    }

    savePlaybackSession();
  } else {
    // DO NOT emit null state to prevent unmounting the Now Playing panel
    // store.dispatch(spotifyActions.setState({ state: null }));
    store.dispatch({ type: 'queue/fetchQueue/fulfilled', payload: [] });
  }
  } catch (e) {}
};

const actuallyPlayTrack = async (track: any) => {
  lastKnownPositionMs = 0;
  try {
    audio.pause();
    audio.currentTime = 0;
  } catch (e) {}
  currentTrack = track;
  audio.src = currentTrack.preview_url;

  // Guarantee that audio volume is never 0 when starting a song
  const currentStoreVolume = store.getState().spotify.volume;
  if (audio.volume === 0 && currentStoreVolume > 0) {
    audio.volume = currentStoreVolume;
  } else if (audio.volume === 0) {
    const saved = getSavedVolume();
    if (saved > 0) {
      audio.volume = saved;
      store.dispatch(spotifyActions.setVolume({ volume: saved }));
    }
  }

  store.dispatch(homeActions.addRecentlyPlayed(currentTrack));
  historyService.recordPlay(currentTrack, currentContextUri);
  updateMediaSession(currentTrack);
  await audio.play().catch(e => console.warn('Playback interrupted:', e));
  dispatchState();
};

const fetchAutoplaySuggestions = async (track: any): Promise<any[]> => {
  if (!track || !track.id) return [];
  try {
    const res = await axios.get(`/songs/${track.id}/suggestions?limit=20`);
    if (res.data?.data?.length > 0) {
      return res.data.data.map((s: any) => mapJioSaavnSongToSpotifyTrack(s));
    }
  } catch (e) {
    console.warn('Failed to fetch suggestions for song', track.id, e);
  }

  // Fallback to primary artist top songs if song suggestions were empty
  const artistId = track.artists?.[0]?.id;
  if (artistId) {
    try {
      const aRes = await axios.get(`/artists?id=${artistId}`);
      const topSongs = aRes.data?.data?.topSongs || [];
      if (topSongs.length > 0) {
        return topSongs
          .filter((s: any) => s.id !== track.id)
          .map((s: any) => mapJioSaavnSongToSpotifyTrack(s));
      }
    } catch (e) {
      console.warn('Failed to fetch artist top songs fallback', artistId, e);
    }
  }
  return [];
};

const resolveNextTrack = async (isUserSkip: boolean = false) => {
  if (!currentTrack || isResolvingNextTrack) return;
  
  isResolvingNextTrack = true;
  try {
    // Push current to history
    if (isUserSkip || repeatMode !== 'one') {
      playbackHistory.push(currentTrack);
    }

  // 1. Check manual queue first
  if (manualQueue.length > 0) {
    const nextManual = manualQueue.shift();
    isPlayingFromManualQueue = true;
    await actuallyPlayTrack(nextManual);
    return;
  }

  // 2. Repeat One (unless it's a user manual skip)
  if (!isUserSkip && repeatMode === 'one') {
    audio.currentTime = 0;
    await actuallyPlayTrack(currentTrack);
    return;
  }

  // 3. Return to context
  isPlayingFromManualQueue = false;
  let activeContext = getActiveContext();
  let nextIndex = currentTrackIndex + 1;

  if (nextIndex >= activeContext.length) {
    if (repeatMode === 'all' && activeContext.length > 0) {
      if (shuffleEnabled) {
        generateNewShuffle(); // Reshuffle for new loop
        activeContext = getActiveContext();
      }
      nextIndex = 0;
    } else if (autoplayEnabled && currentTrack) {
      // Autoplay / Radio Fallback
      try {
        const suggestions = await fetchAutoplaySuggestions(currentTrack);
        if (suggestions.length > 0) {
          sourceContext.push(...suggestions);
          if (shuffleEnabled) {
            shuffledSourceOrder.push(...suggestions);
          }
        } else {
           // No suggestions found, stop playback
           audio.pause();
           return;
        }
      } catch (err) {
        console.error('Failed to fetch autoplay suggestions', err);
        audio.pause();
        return;
      }
    } else {
       // Stop playback
       audio.pause();
       return;
    }
  }

    currentTrackIndex = nextIndex;
    await actuallyPlayTrack(activeContext[currentTrackIndex]);
  } finally {
    isResolvingNextTrack = false;
  }
};

// ---------------------------------------------------------
// EVENT LISTENERS
// ---------------------------------------------------------
let lastDispatchTime = 0;
audio.addEventListener('timeupdate', () => {
  const now = Date.now();
  if (audio.currentTime > 0) {
    lastKnownPositionMs = audio.currentTime * 1000;
  }
  if (now - lastDispatchTime >= 250) {
    dispatchState();
    lastDispatchTime = now;
  }
});
audio.addEventListener('play', () => { dispatchState(); lastDispatchTime = Date.now(); });
audio.addEventListener('pause', () => { dispatchState(); lastDispatchTime = Date.now(); });
audio.addEventListener('ended', () => {
  resolveNextTrack(false).catch(console.error);
});

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    savePlaybackSession(true);
  });
  window.addEventListener('pagehide', () => {
    savePlaybackSession(true);
  });
}

// ---------------------------------------------------------
// EXPORTED ACTIONS
// ---------------------------------------------------------
const fetchPlaybackState = async () => ({ is_playing: !audio.paused });
const transferPlayback = async (deviceId: string) => {};
const getAvailableDevices = async () => ({ devices: [] as Device[] });

const resolveUriToTracks = async (uri: string): Promise<any[]> => {
  const id = uri.split(':').pop() || '';
  const type = uri.split(':')[1];
  
  if (type === 'playlist') {
    if (id.startsWith('custom_playlist_')) {
      const res = await playlistService.getPlaylistItems(id);
      return res.data.items.map((i: any) => i.track);
    } else {
      const res = await axios.get('/playlists?id=' + id + '&limit=500');
      return res.data.data.songs.map((s: any) => mapJioSaavnSongToSpotifyTrack(s));
    }
  } else if (type === 'album') {
    try {
      const res = await axios.get('/albums?id=' + id);
      const songs = res.data?.data?.songs;
      if (songs && songs.length > 0 && !songs[0]?.name?.includes('sample trailer')) {
        return songs.map((s: any) => mapJioSaavnSongToSpotifyTrack(s));
      }
    } catch (e) {}
    try {
      const sRes = await axios.get(`/songs/${id}`);
      const song = Array.isArray(sRes.data?.data) ? sRes.data?.data?.[0] : sRes.data?.data;
      if (song) return [mapJioSaavnSongToSpotifyTrack(song)];
    } catch (e) {}
    return [];
  } else if (type === 'artist') {
    const res = await axios.get('/artists?id=' + id);
    return (res.data?.data?.topSongs || []).map((s: any) => mapJioSaavnSongToSpotifyTrack(s));
  } else if (type === 'episode') {
    try {
      const epRes = await axios.get(`/songs/${id}`);
      const song = Array.isArray(epRes.data?.data) ? epRes.data?.data?.[0] : epRes.data?.data;
      if (song) return [mapJioSaavnSongToSpotifyTrack(song)];
    } catch (e) {}
    return [];
  } else if (type === 'show') {
    try {
      const showRes = await axios.get(`/shows/${id}`);
      if (showRes.data?.data?.episodes?.length > 0) {
        const episodeId = showRes.data.data.episodes[0].id;
        const epRes = await axios.get(`/songs/${episodeId}`);
        const song = Array.isArray(epRes.data?.data) ? epRes.data?.data?.[0] : epRes.data?.data;
        if (song) return [mapJioSaavnSongToSpotifyTrack(song)];
      }
    } catch (e) {}
    return [];
  } else {
    // default to track
    try {
      const res = await axios.get(`/songs/${id}`);
      const song = Array.isArray(res.data?.data) ? res.data?.data?.[0] : res.data?.data;
      if (song) return [mapJioSaavnSongToSpotifyTrack(song)];
    } catch (e) {}
    return [];
  }
};

const startPlayback = async (body: any = {}) => {
  try {
    if (body.shuffle !== undefined) {
      shuffleEnabled = body.shuffle;
    }

    let newContext: any[] = [];
    let songIndex = 0;
    
    if (body.context_uri) {
      currentContextUri = body.context_uri;
    } else if (body.uris && body.uris.length > 0) {
      currentContextUri = body.uris[0];
    } else {
      currentContextUri = '';
    }

    if (body.tracks && Array.isArray(body.tracks) && body.tracks.length > 0) {
      newContext = [...body.tracks];
      if (body.song) {
        const foundIndex = newContext.findIndex((s: any) => s.id === body.song.id || s.uri === body.song.uri);
        if (foundIndex !== -1) songIndex = foundIndex;
      } else if (body.offset && typeof body.offset.position === 'number') {
        songIndex = body.offset.position;
      }
    } else if (body.context_uri) {
      if (body.offset && typeof body.offset.position === 'number') {
        songIndex = body.offset.position;
      }
      
      const songs = await resolveUriToTracks(body.context_uri);

      if (body.song) {
        const foundIndex = songs.findIndex((s: any) => s.id === body.song.id || s.uri === body.song.uri);
        if (foundIndex !== -1) songIndex = foundIndex;
      } else if (body.offset && body.offset.uri) {
        const trackId = body.offset.uri.split(':').pop();
        const foundIndex = songs.findIndex((s: any) => s.id === trackId || s.uri === body.offset.uri);
        if (foundIndex !== -1) songIndex = foundIndex;
      }
      newContext = songs;
    } else if (body.song) {
      newContext = [body.song];
    } else if (body.uris && body.uris.length > 0) {
      const uri = body.uris[0];
      newContext = await resolveUriToTracks(uri);
    } else {
      await audio.play().catch(e => console.warn('Playback interrupted:', e));
      return;
    }

    if (newContext.length > 0) {
      sourceContext = newContext;
      isPlayingFromManualQueue = false;
      manualQueue = [];
      playbackHistory = [];

      let explicitlySelected = !!(body.song || body.offset);

      if (shuffleEnabled) {
        if (!explicitlySelected) {
           songIndex = Math.floor(Math.random() * sourceContext.length);
        }
        // If shuffle is already on when a new context starts, we pick a random first track OR we respect the selected songIndex.
        // If the user explicitly clicked a song (songIndex > 0 or matched), we start with that track.
        const startingTrack = sourceContext[songIndex];
        generateNewShuffle(startingTrack);
        currentTrackIndex = 0; // generateNewShuffle puts the starting track at index 0
      } else {
        currentTrackIndex = songIndex;
      }
      
      await actuallyPlayTrack(getActiveContext()[currentTrackIndex]);

      // If playing a single track (or context is length 1) and autoplay is enabled,
      // prefetch suggestions immediately so that next_tracks is populated for swipe & queue
      if (newContext.length <= 1 && autoplayEnabled) {
        const targetTrack = body.song || newContext[0];
        if (targetTrack) {
          fetchAutoplaySuggestions(targetTrack).then((suggestions) => {
            if (suggestions.length > 0 && sourceContext.length <= 1) {
              sourceContext.push(...suggestions);
              if (shuffleEnabled) {
                shuffledSourceOrder.push(...suggestions);
              }
              dispatchState();
            }
          }).catch((err) => console.warn('Autoplay prefetch failed:', err));
        }
      }
    }
  } catch (err) {
    console.error('Failed to play audio:', err);
  }
};

const pausePlayback = async () => {
  audio.pause();
};

const nextTrack = async () => {
  await resolveNextTrack(true);
};

const previousTrack = async () => {
  if (audio.currentTime > 3 || playbackHistory.length === 0) {
    audio.currentTime = 0;
    lastKnownPositionMs = 0;
    dispatchState();
  } else {
    // Go to previous track in history
    const prev = playbackHistory.pop();
    
    // We also need to step back in the context if we were playing from context
    if (!isPlayingFromManualQueue && currentTrackIndex > 0) {
      currentTrackIndex--;
    } else if (isPlayingFromManualQueue) {
      // If we were playing manual queue, going back to history means we are likely going back to a context track
      const activeContext = getActiveContext();
      const idx = activeContext.findIndex(t => t.id === prev.id);
      if (idx !== -1) {
        isPlayingFromManualQueue = false;
        currentTrackIndex = idx;
      } else {
        manualQueue.unshift(prev);
        isPlayingFromManualQueue = true;
      }
    }
    
    await actuallyPlayTrack(prev);
  }
};

const seekToPosition = async (position_ms: number) => {
  if (typeof position_ms !== 'number' || isNaN(position_ms) || position_ms < 0) return;
  lastKnownPositionMs = position_ms;
  try {
    audio.currentTime = position_ms / 1000;
  } catch (e) {}
  dispatchState();
};

const updateMediaSession = (track: any) => {
  if (typeof window === 'undefined' || !('mediaSession' in navigator) || !track) return;

  const artwork: any[] = [];
  const images = track.album?.images || [];
  if (images.length > 0) {
    images.forEach((img: any) => {
      if (img.url) {
        artwork.push({
          src: img.url,
          sizes: `${img.width || 500}x${img.height || 500}`,
          type: 'image/jpeg',
        });
      }
    });
  } else if (track.image) {
    const src = typeof track.image === 'string' ? track.image : track.image[0]?.url;
    if (src) {
      artwork.push({
        src,
        sizes: '500x500',
        type: 'image/jpeg',
      });
    }
  }

  const artistName = (track.artists || []).map((a: any) => a.name).join(', ') || 'Unknown Artist';

  try {
    navigator.mediaSession.metadata = new MediaMetadata({
      title: track.name || 'Unknown Track',
      artist: artistName,
      album: track.album?.name || '',
      artwork,
    });
  } catch (e) {
    console.warn('Failed to set mediaSession metadata:', e);
  }
};

const initMediaSessionHandlers = () => {
  if (typeof window === 'undefined' || !('mediaSession' in navigator)) return;

  try {
    navigator.mediaSession.setActionHandler('play', () => {
      audio.play().catch(console.warn);
    });
    navigator.mediaSession.setActionHandler('pause', () => {
      audio.pause();
    });
    navigator.mediaSession.setActionHandler('previoustrack', () => {
      previousTrack();
    });
    navigator.mediaSession.setActionHandler('nexttrack', () => {
      nextTrack();
    });
    navigator.mediaSession.setActionHandler('seekto', (details) => {
      if (typeof details.seekTime === 'number' && !isNaN(details.seekTime)) {
        seekToPosition(details.seekTime * 1000);
      }
    });
  } catch (e) {
    console.warn('Failed to set mediaSession handlers:', e);
  }
};

initMediaSessionHandlers();

const setRepeatMode = async (state: 'track' | 'context' | 'off') => {
  if (state === 'track') repeatMode = 'one';
  else if (state === 'context') repeatMode = 'all';
  else repeatMode = 'off';
  dispatchState();
};

const setVolume = async (volume_percent: number) => {
  if (typeof volume_percent === 'number' && !isNaN(volume_percent)) {
    const vol = Math.max(0, Math.min(1, volume_percent / 100));
    audio.volume = vol;
    saveVolume(vol);
    savePlaybackSession();
  }
};

const toggleShuffle = async (state: boolean) => {
  shuffleEnabled = state;
  
  if (shuffleEnabled) {
    const trackToExclude = isPlayingFromManualQueue ? undefined : currentTrack;
    generateNewShuffle(trackToExclude);
    if (!isPlayingFromManualQueue && currentTrack) {
      currentTrackIndex = 0; // Excluded track goes to index 0
    } else {
      currentTrackIndex = 0; // Reset index to 0 if playing from manual queue so it's anchored when queue finishes
    }
  } else {
    if (currentTrack) {
      const idx = sourceContext.findIndex(t => t.id === currentTrack.id);
      if (idx !== -1) currentTrackIndex = idx;
    }
  }
  
  dispatchState();
};

const addToQueue = async (uri: string) => {
  try {
    const tracks = await resolveUriToTracks(uri);
    if (tracks.length > 0) {
      tracks.forEach(track => {
        (track as any).isUserQueued = true;
        manualQueue.push(track); // FIFO append to end
      });
      dispatchState();
    }
  } catch (err) {
    console.error('Failed to add to queue', err);
  }
};

const playNext = async (uri: string) => {
  try {
    const tracks = await resolveUriToTracks(uri);
    if (tracks.length > 0) {
      // Unshift in reverse so they end up in correct order at the front
      for (let i = tracks.length - 1; i >= 0; i--) {
        const track = tracks[i];
        (track as any).isUserQueued = true;
        manualQueue.unshift(track);
      }
      dispatchState();
    }
  } catch (err) {
    console.error('Failed to play next', err);
  }
};

const getRecentlyPlayed = async (params: any) => ({ items: [], limit: 50, offset: 0, total: 0, previous: null, next: null, href: '' } as unknown as Pagination<PlayHistoryObject>);

const reorderQueue = async (sourceIndex: number, destinationIndex: number) => {
  if (sourceIndex === destinationIndex) return;
  if (sourceIndex < 0 || destinationIndex < 0) return;

  const activeContext = getActiveContext();
  const contextStartIndex = currentTrackIndex + 1;
  const remainingContext = contextStartIndex < activeContext.length ? activeContext.slice(contextStartIndex) : [];
  
  const nextTracks = [...manualQueue, ...remainingContext];
  if (sourceIndex >= nextTracks.length || destinationIndex >= nextTracks.length) return;

  // Perform the move
  const [removed] = nextTracks.splice(sourceIndex, 1);
  if (destinationIndex < manualQueue.length) {
    (removed as any).isUserQueued = true;
  } else if (sourceIndex < manualQueue.length && destinationIndex >= manualQueue.length) {
    (removed as any).isUserQueued = false;
  }
  nextTracks.splice(destinationIndex, 0, removed);

  const newManualQueue = nextTracks.filter(t => (t as any).isUserQueued);
  const newRemainingContext = nextTracks.filter(t => !(t as any).isUserQueued);

  manualQueue.length = 0;
  manualQueue.push(...newManualQueue);

  const prefix = activeContext.slice(0, contextStartIndex);
  const newActiveContext = [...prefix, ...newRemainingContext];

  if (shuffleEnabled) {
    shuffledSourceOrder.length = 0;
    shuffledSourceOrder.push(...newActiveContext);
  } else {
    sourceContext.length = 0;
    sourceContext.push(...newActiveContext);
  }

  dispatchState();
};

const reorderUpcomingQueue = async (sourceIndex: number, destinationIndex: number) => {
  return reorderQueue(sourceIndex, destinationIndex);
};

const clearUserQueue = async () => {
  manualQueue = [];
  dispatchState();
};

const removeFromQueue = async (index: number) => {
  if (index < manualQueue.length) {
    manualQueue.splice(index, 1);
  } else {
    const contextOffset = index - manualQueue.length;
    const activeContext = getActiveContext();
    const targetIndex = currentTrackIndex + 1 + contextOffset;
    if (targetIndex < activeContext.length) {
      activeContext.splice(targetIndex, 1);
    }
  }
  dispatchState();
};

const setUpcomingQueue = async (newQueue: any[]) => {
  const activeContext = getActiveContext();
  const contextStartIndex = currentTrackIndex + 1;

  let lastUserQueuedIndex = -1;
  for (let i = newQueue.length - 1; i >= 0; i--) {
    if (newQueue[i]?.isUserQueued) {
      lastUserQueuedIndex = i;
      break;
    }
  }

  const updatedQueue = newQueue.map((track, i) => {
    if (i <= lastUserQueuedIndex) {
      return { ...track, isUserQueued: true };
    }
    return track;
  });

  const newManualQueue = updatedQueue.filter(t => (t as any).isUserQueued);
  const newRemainingContext = updatedQueue.filter(t => !(t as any).isUserQueued);

  manualQueue.length = 0;
  manualQueue.push(...newManualQueue);

  const prefix = activeContext.slice(0, contextStartIndex);
  const newActiveContext = [...prefix, ...newRemainingContext];

  if (shuffleEnabled) {
    shuffledSourceOrder.length = 0;
    shuffledSourceOrder.push(...newActiveContext);
  } else {
    sourceContext.length = 0;
    sourceContext.push(...newActiveContext);
  }

  dispatchState();
};

export const playerService = {
  addToQueue,
  playNext,
  setPlaybackDevice,
  setPlaybackDeviceName,
  fetchPlaybackState,
  transferPlayback,
  startPlayback,
  pausePlayback,
  nextTrack,
  previousTrack,
  setRepeatMode,
  setVolume,
  toggleShuffle,
  seekToPosition,
  getRecentlyPlayed,
  getAvailableDevices,
  reorderQueue,
  reorderUpcomingQueue,
  clearUserQueue,
  removeFromQueue,
  setUpcomingQueue,
  restoreSession: restorePlaybackSession,
  skipToUpcomingQueueItem: async (index: number) => {
    // Determine if it's in manualQueue or context
    if (index < manualQueue.length) {
      const track = manualQueue[index];
      manualQueue.splice(index, 1); // Remove it since we are playing it
      isPlayingFromManualQueue = true;
      if (currentTrack) playbackHistory.push(currentTrack);
      await actuallyPlayTrack(track);
    } else {
      const contextOffset = index - manualQueue.length;
      const activeContext = getActiveContext();
      const targetIndex = currentTrackIndex + 1 + contextOffset;
      if (targetIndex < activeContext.length) {
        isPlayingFromManualQueue = false;
        if (currentTrack) playbackHistory.push(currentTrack);
        currentTrackIndex = targetIndex;
        await actuallyPlayTrack(activeContext[currentTrackIndex]);
      }
    }
  }
};

if (typeof window !== 'undefined') {
  setTimeout(() => {
    restorePlaybackSession();
  }, 0);
}

// For testing purposes
export const _testAccess = {
  getState: () => ({
    sourceContext,
    shuffledSourceOrder,
    manualQueue,
    playbackHistory,
    currentTrackIndex,
    shuffleEnabled,
    repeatMode,
    currentTrack,
    isPlayingFromManualQueue
  }),
  audio,
  setState: (state: any) => {
    if (state.sourceContext) sourceContext = state.sourceContext;
    if (state.shuffledSourceOrder) shuffledSourceOrder = state.shuffledSourceOrder;
    if (state.manualQueue) manualQueue = state.manualQueue;
    if (state.playbackHistory) playbackHistory = state.playbackHistory;
    if (state.currentTrackIndex !== undefined) currentTrackIndex = state.currentTrackIndex;
    if (state.shuffleEnabled !== undefined) shuffleEnabled = state.shuffleEnabled;
    if (state.repeatMode !== undefined) repeatMode = state.repeatMode;
    if (state.currentTrack !== undefined) currentTrack = state.currentTrack;
    if (state.isPlayingFromManualQueue !== undefined) isPlayingFromManualQueue = state.isPlayingFromManualQueue;
  },
  resetState: () => {
    sourceContext = [];
    shuffledSourceOrder = [];
    manualQueue = [];
    playbackHistory = [];
    currentTrackIndex = -1;
    shuffleEnabled = false;
    repeatMode = 'off';
    currentTrack = null;
    isPlayingFromManualQueue = false;
  }
};
